import { Injectable, NgZone } from '@angular/core';
import { tap } from 'rxjs/operators';
// Store
import { Action, Selector, State, StateContext } from '@ngxs/store';
// Models
import { UserContextModel } from './user-context.model';
// Actions
import {
	Logout,
	UpdateSchemaUrl,
	UpdateRulesChecked,
	UpdateOrderRulesChecked,
	LoginAppUpdate,
	UserSetInactive,
	UserSetActive,
	LogoutAppUpdate,
	SetUserData,
	SetLoginMessage,
	SetIsHasPermissions,
	AddMenuItem,
	RemoveMenuItemByProperty,
	SetCloudFrontSignCookies,
	UpdateToken,
	SetIsPublicUser
} from './action/auth.action';
// Service
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthService } from './service/auth.service';
import jwtDecode from 'jwt-decode';
import { Observable } from 'rxjs';

@State<UserContextModel>({
	name: 'user',
	defaults: {
		loginFail: false,
		isLoading: false
	}
})
@Injectable()
export class UserContextState {
	constructor(
		private authService: AuthService,
		private ngZone: NgZone,
		private oidcSecurityService: OidcSecurityService
	) {
	}

	/**
	 * Get user context.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getUserContext(state: UserContextModel): UserContextModel {
		return state;
	}

	/**
	 * Returns navigation items.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getNavItems(state: UserContextModel): any {
		return state.navItems;
	}

	/**
	 * Returns token.
	 * @param state: UserContextModel
	 */
	@Selector()
	static token(state: UserContextModel): string | boolean {
		let response: string | boolean = false;

		if (state.token && state.token !== null && state.token !== '') {
			const now = Math.floor(Date.now() / 1000);
			const {exp}: any = jwtDecode(state.token);

			if (now < exp) {
				response = state.token;
			}
		}

		return response;
	}

	/**
	 * Returns token.
	 * @param state: UserContextModel
	 */
	@Selector()
	static permission(state: UserContextModel): any {
		return state.permission;
	}

	/**
	 * Returns the current user url.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getLoginMsg(state: UserContextModel): string {
		return state.loginMessage;
	}

	/**
	 * Returns the current user organization.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getOrganizationName(state: UserContextModel): string {
		return state.organizationName;
	}

	/**
	 * Returns the current user organization Type.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getOrganizationType(state: UserContextModel): string {
		return state.organizationType;
	}

	/**
	 * Returns the current user Type.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getOrganizationUserType(state: UserContextModel): string {
		return state.userType;
	}

	/**
	 * Returns the current user organization ID.
	 * @param state: UserContextModel
	 */
	@Selector()
	static getOrganizationId(state: UserContextModel): string {
		return state.organizationId;
	}

	@Selector()
	static isPublicUser(state: UserContextModel): boolean {
		return state.isPublicUser;
	}

	/**
	 * Set the user info from the ids
	 * @param ctx
	 * @param param1
	 */
	@Action(SetUserData)
	setUserData(ctx: StateContext<UserContextModel>, {token}: SetUserData): any {
		return this.authService.setUserData(token).pipe(
			tap(result => {
				ctx.patchState({
					...result,
					isPublicUser: false
				});
			})
		);
	}

	/**
	 * Set the isPublicUser state
	 * @param ctx
	 * @param param1
	 */
	@Action(SetIsPublicUser)
	setIsPublicUser(ctx: StateContext<UserContextModel>, {isPublicUser}: SetIsPublicUser): any {
		return ctx.patchState({
			isPublicUser: isPublicUser
		});
	}

	/**
	 * Update Token & UserData from Token info
	 * @param ctx
	 * @param {token} string
	 */
	@Action(UpdateToken)
	updateToken(ctx: StateContext<UserContextModel>, {token}: UpdateToken): Observable<UserContextModel> {
		return this.authService.getUserDataFromToken(token).pipe(
			tap(result => {
				ctx.patchState({
					...result
				});
			})
		);
	}

	/**
	 * Set the user url
	 * @param ctx
	 * @param currentModule
	 */
	@Action(SetLoginMessage)
	setLoginMessage(ctx: StateContext<UserContextModel>, {loginMessage}: SetLoginMessage): any {
		const state = ctx.getState();
		ctx.setState({
			...state,
			loginMessage
		});
	}

	/**
	 * Update the App by calling the Proxy
	 * @param ctx
	 * @param payload
	 */
	@Action(LoginAppUpdate)
	loginAppUpdate(ctx: StateContext<UserContextModel>, {appState}: LoginAppUpdate): any {
		ctx.patchState({
			isLoading: true
		});
		return this.authService.loginAppUpdate(appState).pipe(
			tap(
				result => {
					const loginFail = false;
					ctx.patchState({
						...result,
						loginFail,
						isLoading: false
					});
				},
				() => {
					ctx.setState({
						loginFail: true,
						isLoading: false
					});
				}
			)
		);
	}

	/**
	 * Logout.
	 * @param ctx: UserContextModel
	 */
	@Action(Logout)
	logout(ctx: StateContext<UserContextModel>, {force}: Logout): void {
		const state = ctx.getState();
		const {currentUrl, currentModule} = state;

		this.ngZone.run(() => {
			ctx.setState({});
		});
		if (!force) {
			ctx.setState({
				currentUrl,
				currentModule
			});
		}
	}

	/**
	 * Logout for App Update.
	 * @param ctx: UserContextModel
	 */
	@Action(LogoutAppUpdate)
	logoutAppUpdate(ctx: StateContext<UserContextModel>): void {
		this.ngZone.run(() => {
			ctx.setState({
				isActive: true
			});
			this.oidcSecurityService.authorize();
		});
	}

	/**
	 * Set user Active.
	 * @param ctx: UserContextModel
	 */
	@Action(UserSetActive)
	userSetActive(ctx: StateContext<UserContextModel>): void {
		const state = ctx.getState();
		ctx.setState({
			...state,
			isActive: true
		});
	}

	/**
	 * Set user inactive.
	 * @param ctx: UserContextModel
	 */
	@Action(UserSetInactive)
	userSetInactive(ctx: StateContext<UserContextModel>): void {
		const state = ctx.getState();
		ctx.setState({
			...state,
			isActive: false
		});
	}

	/**
	 * Set user inactive.
	 * @param ctx: UserContextModel
	 */
	@Action(SetIsHasPermissions)
	SetIsHasPermissions(
		ctx: StateContext<UserContextModel>,
		{requestPermissions}: SetIsHasPermissions
	): any {
		const state = ctx.getState();
		ctx.setState({
			...state,
			requestPermissions
		});
	}

	/**
	 * update navItems with the rules counter when we updated a single rule
	 * @param ctx
	 * @param payload
	 */
	@Action(UpdateRulesChecked)
	updateRulesChecked(ctx: StateContext<UserContextModel>, {payload}: UpdateRulesChecked): void {
		const state = ctx.getState();
		const {uiSection, ruleCheck} = payload;
		const badge = ruleCheck.toString();
		const navItems = state.navItems.map((n: any) => {
			const {path} = n;
			if (path) {
				if (path === uiSection) {
					return {
						...n,
						badge
					};
				}
			}
			return n;
		});
		ctx.patchState({
			...state,
			navItems
		});
	}

	/**
	 * update navItems with the rules counter when we load the rule set
	 * @param ctx
	 * @param payload
	 */
	@Action(UpdateOrderRulesChecked)
	updateOrderRulesChecked(
		ctx: StateContext<UserContextModel>,
		{payload}: UpdateOrderRulesChecked
	): void {
		const state = ctx.getState();
		const navItems = state.navItems.map((n: any) => {
			const {path} = n;
			if (path) {
				const sc = payload.filter(u => u.uiSection === path.toUpperCase()).pop();
				if (sc) {
					return {
						...n,
						badge: sc.check.toString()
					};
				}
			}
			return n;
		});
		ctx.patchState({
			...state,
			navItems
		});
	}

	/**
	 * update schema route with orderId value
	 * @param ctx
	 * @param payload
	 */
	@Action(UpdateSchemaUrl)
	updateSchemaUrl(ctx: StateContext<UserContextModel>, {payload}: UpdateSchemaUrl): void {
		const state = ctx.getState();
		const {schema: sch} = state;
		const {orderId, schema, revised, isCMPForm} = payload;
		let newNavItem = Object.assign(state.navItems, {});
		let schemaNav = sch.filter((s: any) => s.type === schema);
		if (!schemaNav || schemaNav.length === 0) {
			if ( /CVReview/.test(schema) && isCMPForm) {
				schemaNav = sch.filter((s: any) => s.type === 'CVReviewAltVal');
			}
		}
		const prevSchema = newNavItem.filter((n: any) => n.schema === schema);
		const indexOverview = newNavItem.findIndex((n: any) => n.path === 'Overview');
		if (revised) {
			const rvs = sch.filter((s: any) => s.type === 'revised');
			const {navItems: items} = rvs[0];
			newNavItem = newNavItem.filter((n: any) => !n.isRevised);

			items.forEach((n: any) => {
				newNavItem.splice(indexOverview, 0, n);
			});
		}
		if (!revised) {
			newNavItem = newNavItem.filter((n: any) => !n.isRevised);
		}

		if (schemaNav.length && !prevSchema.length) {
			const {navItems: nv} = schemaNav[0];
			newNavItem = newNavItem.filter((n: any) => !n.schema);
			nv.forEach((n: any) => {
				newNavItem.splice(indexOverview - 1, 0, n);
			});
		}

		if (!schemaNav.length) {
			newNavItem = newNavItem.filter((n: any) => !n.schema);
		}

		const navItems = newNavItem.map((n: any) => {
			const {module: m} = n;
			if (m === 'quality') {
				const route = `${n.basePath}${n.path}/${orderId}`;
				return {
					...n,
					route
				};
			}
			return n;
		});

		ctx.patchState({
			...state,
			navItems
		});
	}

	/**
	 * Add a menu Item
	 * @param ctx
	 * @param payload
	 */
	@Action(AddMenuItem)
	addMenuItem(ctx: StateContext<UserContextModel>, {payload}: AddMenuItem): void {
		const state = ctx.getState();
		// Add a new Element to the Menu
		const newNavItems = Object.assign([], state.navItems);
		const menuItem = newNavItems.find((item: any) => {
			return item.route === payload.item.route;
		});
		if (!menuItem) {
			newNavItems.push(payload.item);
		}

		ctx.patchState({
			...state,
			navItems: newNavItems
		});
	}

	/**
	 * Remove a menu Item by property/value
	 * @param ctx
	 * @param payload
	 */
	@Action(RemoveMenuItemByProperty)
	removeMenuItemByIndexes(ctx: StateContext<UserContextModel>, {payload}: RemoveMenuItemByProperty): void {
		const state = ctx.getState();
		// Add a new Element to the Menu
		let newNavItems = Object.assign(state.navItems, {});
		newNavItems = newNavItems.filter((menuItem: any) => {
			return menuItem[payload.property] !== payload.value;
		});
		ctx.patchState({
			...state,
			navItems: newNavItems
		});
	}

	/**
	 * Set the cookie from cloud front
	 * @param ctx
	 * @param token
	 */
	@Action(SetCloudFrontSignCookies)
	setCloudFrontSignCookies(ctx: StateContext<UserContextModel>, {token}: SetCloudFrontSignCookies): any {
		return this.authService.setCloudFrontSignCookies(token)

	}
}
